import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as DuplicateIcon,
  Delete as DeleteIcon,
  PlaylistAdd as QuickAddIcon,
} from '@mui/icons-material';
import Section, { nativeSelectProps, shrinkLabelProps } from './ScheduleFormSection';
import QuickAddGamesDialog from './QuickAddGamesDialog';
import { GAME_TYPE_OPTIONS, createEmptyExtraGame } from './scheduleFormModel';

const TeamSelect = ({ label, ariaLabel, value, onChange, teams, disabled, emptyLabel }) => (
  <TextField
    label={label}
    select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    fullWidth
    slotProps={{ ...nativeSelectProps, htmlInput: { 'aria-label': ariaLabel } }}
  >
    <option value="">{emptyLabel}</option>
    {teams.map((t) => (
      <option key={t.id} value={t.id}>{t.name}</option>
    ))}
  </TextField>
);

const LabelField = ({ label, ariaLabel, value, onChange, placeholder, disabled }) => (
  <TextField
    label={label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    disabled={disabled}
    fullWidth
    slotProps={{ ...shrinkLabelProps, htmlInput: { maxLength: 100, 'aria-label': ariaLabel } }}
  />
);

const ExtraGamesSection = ({
  games = [],
  onChange,
  teams = [],
  venues = [],
  gameLengthMinutes,
  disabled = false,
}) => {
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  const venueById = useMemo(() => {
    const map = new Map();
    venues.forEach((v) => map.set(String(v.id), v));
    return map;
  }, [venues]);

  const updateGame = (index, patch) => {
    onChange(games.map((g, i) => (i === index ? { ...g, ...patch } : g)));
  };

  const duplicateGame = (index) => {
    const next = [...games];
    next.splice(index + 1, 0, { ...games[index] });
    onChange(next);
  };

  return (
    <Section
      title="Playoffs & other games"
      description="One-off games of any type. Leave both teams as TBD when they aren't known yet — TBD games are added to the league schedule as placeholders and can be converted to real games later."
      actions={(
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            size="small"
            startIcon={<QuickAddIcon />}
            onClick={() => setQuickAddOpen(true)}
            disabled={disabled}
          >
            Quick add
          </Button>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onChange([...games, createEmptyExtraGame()])}
            disabled={disabled}
          >
            Add game
          </Button>
        </Stack>
      )}
    >
      {games.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No playoff or other games.</Typography>
      ) : (
        <Stack spacing={2}>
          {games.map((game, index) => {
            const n = index + 1;
            const isBye = game.gameType === 'Bye';
            const venue = venueById.get(String(game.venueId));
            const fields = venue?.fields || [];
            return (
              <Paper
                key={index}
                variant="outlined"
                sx={{ p: { xs: 1.5, sm: 1 }, bgcolor: 'grey.50' }}
                data-testid="extra-game-row"
              >
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: { xs: 'stretch', md: 'center' } }}>
                    <TextField
                      label="Date"
                      type="date"
                      value={game.date}
                      onChange={(e) => updateGame(index, { date: e.target.value })}
                      disabled={disabled}
                      required
                      fullWidth
                      slotProps={{ ...shrinkLabelProps, htmlInput: { 'aria-label': `Game ${n} date` } }}
                    />
                    <TextField
                      label="Start time"
                      type="time"
                      value={game.startTime}
                      onChange={(e) => updateGame(index, { startTime: e.target.value })}
                      disabled={disabled}
                      required
                      fullWidth
                      slotProps={{ ...shrinkLabelProps, htmlInput: { step: 300, 'aria-label': `Game ${n} start time` } }}
                    />
                    <TextField
                      label="Game type"
                      select
                      value={game.gameType}
                      onChange={(e) => updateGame(index, { gameType: e.target.value })}
                      disabled={disabled}
                      fullWidth
                      slotProps={{ ...nativeSelectProps, htmlInput: { 'aria-label': `Game ${n} type` } }}
                    >
                      {GAME_TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </TextField>
                    {!isBye && (
                      <>
                        <TextField
                          label="Venue"
                          select
                          value={game.venueId}
                          onChange={(e) => updateGame(index, { venueId: e.target.value, fieldId: '' })}
                          disabled={disabled}
                          required
                          fullWidth
                          slotProps={{ ...nativeSelectProps, htmlInput: { 'aria-label': `Game ${n} venue` } }}
                        >
                          <option value="">Choose a venue…</option>
                          {venues.map((v) => (
                            <option key={v.id} value={v.id}>{v.name}</option>
                          ))}
                        </TextField>
                        <TextField
                          label="Field (optional)"
                          select
                          value={game.fieldId}
                          onChange={(e) => updateGame(index, { fieldId: e.target.value })}
                          disabled={disabled || !venue || fields.length === 0}
                          fullWidth
                          slotProps={{ ...nativeSelectProps, htmlInput: { 'aria-label': `Game ${n} field` } }}
                        >
                          <option value="">{venue && fields.length === 0 ? 'No fields' : 'Any field'}</option>
                          {fields.map((f) => (
                            <option key={f.id} value={f.id}>{f.name}</option>
                          ))}
                        </TextField>
                      </>
                    )}
                  </Stack>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: { xs: 'stretch', md: 'center' } }}>
                    <TeamSelect
                      label={isBye ? 'Team' : 'Home'}
                      ariaLabel={`Game ${n} home team`}
                      value={game.homeTeamId}
                      onChange={(homeTeamId) => updateGame(index, { homeTeamId })}
                      teams={teams}
                      disabled={disabled}
                      emptyLabel={isBye ? 'Choose a team…' : 'TBD'}
                    />
                    {!isBye && !game.homeTeamId && (
                      <LabelField
                        label="Home label"
                        ariaLabel={`Game ${n} home label`}
                        value={game.homeLabel}
                        onChange={(homeLabel) => updateGame(index, { homeLabel })}
                        placeholder="e.g. 1st Pool A"
                        disabled={disabled}
                      />
                    )}
                    {!isBye && (
                      <TeamSelect
                        label="Away"
                        ariaLabel={`Game ${n} away team`}
                        value={game.awayTeamId}
                        onChange={(awayTeamId) => updateGame(index, { awayTeamId })}
                        teams={teams}
                        disabled={disabled}
                        emptyLabel="TBD"
                      />
                    )}
                    {!isBye && !game.awayTeamId && (
                      <LabelField
                        label="Away label"
                        ariaLabel={`Game ${n} away label`}
                        value={game.awayLabel}
                        onChange={(awayLabel) => updateGame(index, { awayLabel })}
                        placeholder="e.g. 2nd Pool B"
                        disabled={disabled}
                      />
                    )}
                    <Box sx={{ alignSelf: { xs: 'flex-end', md: 'center' } }}>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Duplicate game">
                          <span>
                            <IconButton aria-label={`Duplicate game ${n}`} onClick={() => duplicateGame(index)} disabled={disabled}>
                              <DuplicateIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Remove game">
                          <span>
                            <IconButton
                              aria-label={`Remove game ${n}`}
                              onClick={() => onChange(games.filter((_, i) => i !== index))}
                              disabled={disabled}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Box>
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      <QuickAddGamesDialog
        open={quickAddOpen}
        onClose={() => setQuickAddOpen(false)}
        onAdd={(rows) => {
          onChange([...games, ...rows]);
          setQuickAddOpen(false);
        }}
        venues={venues}
        defaultGapMinutes={gameLengthMinutes}
      />
    </Section>
  );
};

export default ExtraGamesSection;
